import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteAdminChatThread,
  fetchAdminChatThread,
  listAdminChatThreads,
  renameAdminChatThread,
  type AdminChatStoredMessage,
  type AdminChatThreadSummary,
} from "../api/crm";

const THREAD_LIST_KEY = ["admin-chat-threads"];
const threadDetailKey = (id: string) => ["admin-chat-thread", id];

/** List of the admin's threads, ordered by most-recent activity. */
export function useAdminChatThreads(opts: { enabled?: boolean } = {}) {
  return useQuery<{ threads: AdminChatThreadSummary[] }>({
    queryKey: THREAD_LIST_KEY,
    queryFn: () => listAdminChatThreads(),
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });
}

/** Full message list for a specific thread. */
export function useAdminChatThreadMessages(threadId: string | null) {
  return useQuery<{
    thread: AdminChatThreadSummary;
    messages: AdminChatStoredMessage[];
  }>({
    queryKey: threadDetailKey(threadId ?? "none"),
    queryFn: () => fetchAdminChatThread(threadId as string),
    enabled: !!threadId,
    staleTime: 0, // always re-fetch when switching threads
  });
}

export function useDeleteAdminChatThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => deleteAdminChatThread(threadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: THREAD_LIST_KEY });
    },
  });
}

export function useRenameAdminChatThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      renameAdminChatThread(threadId, title),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: THREAD_LIST_KEY });
    },
  });
}

/** Helper to invalidate the thread list — call after a successful send so
 *  a freshly-created thread shows up in the sidebar without a refresh. */
export function useInvalidateAdminChatThreads() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: THREAD_LIST_KEY });
  };
}
